import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ds';
import { useBranch } from '@/contexts/BranchContext';
import { useFormatters } from '@/hooks/useFormatters';

/**
 * Shown on "All branches" when the clinic works in more than one currency.
 *
 * Two things the owner needs to know there, and nowhere else:
 *   - the consolidated pages (Finance Overview, statements, Accounts) are in
 *     the base currency, converted at a stated rate;
 *   - every other report adds amounts in ONE currency only, so it needs a
 *     branch — the API refuses (409 CURRENCY_SCOPE_REQUIRED) rather than add
 *     EGP to USD, and the refusal lands here as an event.
 */
export function CurrencyScopeBanner() {
  const { t } = useTranslation();
  const { isAllBranches, branches } = useBranch();
  const { baseCurrency, isMultiCurrency } = useFormatters();
  const [refusedAt, setRefusedAt] = useState<string | null>(null);

  useEffect(() => {
    const onRefused = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url ?? '';
      setRefusedAt(url);
    };
    window.addEventListener('dc:currency-scope-required', onRefused);
    return () =>
      window.removeEventListener('dc:currency-scope-required', onRefused);
  }, []);

  // A branch was chosen: the refusal no longer applies. Adjusted during
  // render (React's pattern for state that follows a prop), not in an effect.
  const [wasAllBranches, setWasAllBranches] = useState(isAllBranches);
  if (wasAllBranches !== isAllBranches) {
    setWasAllBranches(isAllBranches);
    if (!isAllBranches) setRefusedAt(null);
  }

  if (!isMultiCurrency || !isAllBranches) return null;

  const currencies = [
    ...new Set(branches.map((b) => b.currencyCode || baseCurrency)),
  ].sort();

  return (
    <div className="px-4 pt-3 md:px-6" data-testid="currency-scope-banner">
      <Alert
        tone={refusedAt ? 'warning' : 'info'}
        title={
          refusedAt
            ? t('currency.scopeRequiredTitle', {
                defaultValue: 'Choose a branch to see this report',
              })
            : t('currency.multiTitle', {
                currencies: currencies.join(', '),
                defaultValue: 'Your branches work in {{currencies}}',
              })
        }
      >
        {refusedAt
          ? t('currency.scopeRequiredBody', {
              base: baseCurrency,
              defaultValue:
                'This report adds amounts together, and it only does that within one currency. Pick a branch in the switcher above. Finance Overview, the statements and Accounts show every branch converted to {{base}}.',
            })
          : t('currency.multiBody', {
              base: baseCurrency,
              defaultValue:
                'Totals for all branches are converted to {{base}} at the stated rate. Reports that cannot convert ask you to choose a branch.',
            })}
      </Alert>
    </div>
  );
}
